import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import {
  GET_START_MESSAGE_ANALYTICS,
  GET_CONVERSATION_START_MESSAGE_ANALYTICS,
  GET_TENANT_START_MESSAGE_ANALYTICS,
} from "../../services/graphql/start-message-operations";
import { useAuthStore } from "../../stores/auth-store";

interface StartMessageAnalyticsProps {
  messageId?: string;
  conversationId?: string;
  showTenantSummary?: boolean;
  dateRange?: {
    from: string;
    to: string;
  };
}

interface DeviceBreakdown {
  PHONE: number;
  DESKTOP: number;
  TABLET: number;
  OTHER: number;
}

interface DailyUsage {
  date: string;
  usage: number;
}

interface ConversationUsage {
  conversationId: string;
  usage: number;
}

interface MessageAnalytics {
  messageId: string;
  totalUsage: number;
  smsUsage: number;
  chatUsage: number;
  deviceBreakdown: DeviceBreakdown;
  conversationBreakdown: ConversationUsage[];
  dailyUsage: DailyUsage[];
}

interface ConversationAnalytics {
  conversationId: string;
  totalMessages: number;
  totalUsage: number;
  averageUsagePerMessage: number;
  messageBreakdown: Array<{
    messageId: string;
    messageName: string;
    usage: number;
    smsUsage: number;
    chatUsage: number;
  }>;
}

interface TenantAnalytics {
  tenantId: string;
  totalMessages: number;
  totalUsage: number;
  smsUsage: number;
  chatUsage: number;
  deviceBreakdown: DeviceBreakdown;
  topMessages: Array<{
    messageId: string;
    messageName: string;
    usage: number;
  }>;
}

export function StartMessageAnalytics({
  messageId,
  conversationId,
  showTenantSummary = false,
  dateRange,
}: StartMessageAnalyticsProps): React.ReactElement {
  // Get auth state for skip conditions
  const { user, isAuthenticated, isLoading } = useAuthStore();
  const tenantId = user?.tenantId;

  const [selectedPeriod, setSelectedPeriod] = useState<string>("30d");

  // Calculate date range based on selected period
  const getDateRange = () => {
    if (dateRange) return dateRange;

    const endDate = new Date();
    const startDate = new Date();

    switch (selectedPeriod) {
      case "7d":
        startDate.setDate(endDate.getDate() - 7);
        break;
      case "30d":
        startDate.setDate(endDate.getDate() - 30);
        break;
      case "90d":
        startDate.setDate(endDate.getDate() - 90);
        break;
      default:
        startDate.setDate(endDate.getDate() - 30);
    }

    return {
      from: startDate.toISOString().split("T")[0],
      to: endDate.toISOString().split("T")[0],
    };
  };

  const currentDateRange = getDateRange();

  // Query for message analytics
  const {
    data: messageData,
    loading: messageLoading,
    error: messageError,
  } = useQuery(GET_START_MESSAGE_ANALYTICS, {
    variables: {
      messageId,
      dateFrom: currentDateRange.from,
      dateTo: currentDateRange.to,
    },
    skip: !messageId || !isAuthenticated || !tenantId,
  });

  // Query for conversation analytics
  const {
    data: conversationData,
    loading: conversationLoading,
    error: conversationError,
  } = useQuery(GET_CONVERSATION_START_MESSAGE_ANALYTICS, {
    variables: {
      conversationId,
      dateFrom: currentDateRange.from,
      dateTo: currentDateRange.to,
    },
    skip: !conversationId || !isAuthenticated || !tenantId,
  });

  // Query for tenant analytics
  const {
    data: tenantData,
    loading: tenantLoading,
    error: tenantError,
  } = useQuery(GET_TENANT_START_MESSAGE_ANALYTICS, {
    variables: {
      dateFrom: currentDateRange.from,
      dateTo: currentDateRange.to,
    },
    skip: !showTenantSummary || !isAuthenticated || !tenantId,
  });

  const analytics: MessageAnalytics | undefined =
    messageData?.getStartMessageAnalytics;
  const conversationAnalytics: ConversationAnalytics | undefined =
    conversationData?.getConversationStartMessageAnalytics;
  const tenantAnalytics: TenantAnalytics | undefined =
    tenantData?.getTenantStartMessageAnalytics;

  const loading = messageLoading || conversationLoading || tenantLoading;
  const error = messageError || conversationError || tenantError;

  // Format device type for display
  const formatDeviceType = (device: string) => {
    switch (device) {
      case "PHONE":
        return "Phone";
      case "DESKTOP":
        return "Desktop";
      case "TABLET":
        return "Tablet";
      case "OTHER":
        return "Other";
      default:
        return device;
    }
  };

  // Get device icon
  const getDeviceIcon = (device: string) => {
    switch (device) {
      case "PHONE":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z"
            />
          </svg>
        );
      case "DESKTOP":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        );
      case "TABLET":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
        );
      default:
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        );
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="text-center">
          <div className="w-12 h-12 bg-red-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error loading analytics
          </h3>
          <p className="text-gray-500">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="bg-white rounded-lg shadow border p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Analytics</h3>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Period:</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tenant Summary */}
      {showTenantSummary && tenantAnalytics && (
        <div className="bg-white rounded-lg shadow border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Overall Summary
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Messages
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tenantAnalytics.totalMessages}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Usage
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tenantAnalytics.totalUsage}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">SMS Usage</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tenantAnalytics.smsUsage}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Chat Usage
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tenantAnalytics.chatUsage}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Top Messages */}
          {tenantAnalytics.topMessages.length > 0 && (
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">
                Top Performing Messages
              </h4>
              <div className="space-y-2">
                {tenantAnalytics.topMessages
                  .slice(0, 5)
                  .map((message, index) => (
                    <div
                      key={message.messageId}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center">
                        <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-medium mr-3">
                          {index + 1}
                        </span>
                        <span className="font-medium text-gray-900">
                          {message.messageName}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-gray-600">
                        {message.usage} uses
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Message Analytics */}
      {analytics && (
        <div className="bg-white rounded-lg shadow border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Message Performance
          </h3>

          {/* Usage Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Usage
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.totalUsage}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">SMS Usage</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.smsUsage}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Chat Usage
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.chatUsage}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Device Breakdown */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-3">
              Device Breakdown
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(analytics.deviceBreakdown).map(
                ([device, count]) => (
                  <div
                    key={device}
                    className="flex items-center p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="mr-3 text-gray-600">
                      {getDeviceIcon(device)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDeviceType(device)}
                      </p>
                      <p className="text-lg font-bold text-gray-700">{count}</p>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Daily Usage Chart */}
          {analytics.dailyUsage.length > 0 && (
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">
                Daily Usage Trend
              </h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-end space-x-1 h-32">
                  {analytics.dailyUsage.map((day, index) => {
                    const maxUsage = Math.max(
                      ...analytics.dailyUsage.map((d) => d.usage)
                    );
                    const height =
                      maxUsage > 0 ? (day.usage / maxUsage) * 100 : 0;

                    return (
                      <div
                        key={day.date}
                        className="flex-1 flex flex-col items-center"
                      >
                        <div
                          className="w-full bg-purple-500 rounded-t"
                          style={{ height: `${height}%` }}
                          title={`${day.date}: ${day.usage} uses`}
                        ></div>
                        <span className="text-xs text-gray-500 mt-1 transform rotate-45 origin-left">
                          {new Date(day.date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Conversation Analytics */}
      {conversationAnalytics && (
        <div className="bg-white rounded-lg shadow border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Conversation Performance
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Messages
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {conversationAnalytics.totalMessages}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Usage
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {conversationAnalytics.totalUsage}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Avg per Message
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {Math.round(conversationAnalytics.averageUsagePerMessage)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Message Breakdown */}
          {conversationAnalytics.messageBreakdown.length > 0 && (
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3">
                Message Performance
              </h4>
              <div className="space-y-3">
                {conversationAnalytics.messageBreakdown.map((message) => (
                  <div
                    key={message.messageId}
                    className="p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-gray-900">
                        {message.messageName}
                      </h5>
                      <span className="text-sm font-medium text-gray-600">
                        {message.usage} total uses
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>SMS: {message.smsUsage}</span>
                      <span>•</span>
                      <span>Chat: {message.chatUsage}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
